import { GuidValidationResponse } from './models/guid-validation-response.model';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MainNavComponent } from "./components/main-nav/main-nav.component";
import { SessionTimeoutService } from './services/session-timeout.service';
import { MsalBroadcastService, MsalService, } from '@azure/msal-angular';
import { AuthenticationResult, AccountInfo } from '@azure/msal-browser';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { LoadingService } from './services/loading.service';
import { ClaimsService } from './services/claims.service';
import { environment } from '../environments/environment';
import { StateService } from './services/state.service';
import { Router, RouterModule } from '@angular/router';
import { switchMap } from 'rxjs/operators';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: true,
    imports: [MatProgressSpinnerModule, RouterModule, MainNavComponent]
})

export class AppComponent implements OnInit, OnDestroy {
    private readonly _destroying$ = new Subject<void>();
    loading: boolean = false;
    isIframe = false;
    loginDisplay = false;
    isSigningUp = false;

    constructor(
        private stateService: StateService,
        private loadingService: LoadingService,
        private authService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        private sessionTimeoutService: SessionTimeoutService,
        private router: Router,
        private claimsService: ClaimsService
    ) { }

    async ngOnInit(): Promise<void> {
        console.log(environment.production ? 'Production!' : 'Development!');
        this.isIframe = window !== window.parent && !window.opener;

        this.initializeSigningUpState();
        await this.initializeAuthService();

        if (!this.handleAccountGuidOrRedirect()) {
            return;
        }

        this.setLoginDisplay();
        this.listenToLoading();
        this.subscribeToMsalEvents();
        this.sessionTimeoutService.resetTimers();
    }

    private initializeSigningUpState(): void {
        const storedSigningUp = localStorage.getItem('signingUp');
        const initialSigningUp = storedSigningUp ? JSON.parse(storedSigningUp) : false;

        this.stateService.isSigningUp = initialSigningUp;
        this.stateService.$isSigningUp.subscribe((isSigningUp) => {
            this.updateSigningUpState(isSigningUp);
        });
    }

    private updateSigningUpState(isSigningUp: boolean): void {
        if (this.isSigningUp !== isSigningUp) {
            this.isSigningUp = isSigningUp;
            localStorage.setItem('signingUp', JSON.stringify(isSigningUp));
        }
    }

    private async initializeAuthService(): Promise<void> {
        await this.authService.instance.initialize();

        const activeAccount = this.authService.instance.getActiveAccount();
        if (activeAccount) {
            this.handleActiveAccount(activeAccount);
            return;
        }

        this.authService.handleRedirectObservable().subscribe({
            next: (response: AuthenticationResult) => this.handleRedirectResponse(response),
            error: (error) => console.error('Redirect login failed:', error)
        });
    }

    private handleActiveAccount(activeAccount: AccountInfo): void {
        const stateServiceGuid = this.stateService.guid?.toUpperCase();
        const currentGuid = this.extractGuidFromUrl();
        const caseReference = activeAccount.idTokenClaims?.['extension_CaseReference'];

        if (caseReference) {
            this.validateGuidOrRedirect(stateServiceGuid, currentGuid, caseReference.toString());
        }
        else {
            this.redirectToLogin(stateServiceGuid || currentGuid || environment.defaultGuid.toUpperCase());
        }
    }

    private validateGuidOrRedirect(stateServiceGuid: string | undefined, currentGuid: string | null, caseReference: string): void {
        const guidToValidate = stateServiceGuid || currentGuid;
        if (this.isValidGuid(guidToValidate) && guidToValidate !== environment.defaultGuid.toUpperCase()) {
            this.claimsService.validateGuid(guidToValidate!).pipe(
                switchMap((validatedCaseReference: GuidValidationResponse) => this.handleGuidValidation(validatedCaseReference, caseReference, guidToValidate!))
            ).subscribe();
        }
        else {
            this.proceedWithoutGuidValidation();
        }
    }

    private handleGuidValidation(validatedCaseReference: GuidValidationResponse, caseReference: string, guidToValidate: string): [] {
        if (validatedCaseReference.claimReference!.toUpperCase() === caseReference.toUpperCase()) {
            this.loginDisplay = true;
            this.updateLogoutState(false);
        }
        else {
            this.redirectToLogin(guidToValidate);
        }

        return [];
    }

    private proceedWithoutGuidValidation(): void {
        this.loginDisplay = true;
        this.updateLogoutState(false);
    }

    private redirectToLogin(guid: string): void {
        const redirectUrl = `/${guid}/login`;
        this.updateLogoutState(true);
        this.authService.logoutRedirect({ postLogoutRedirectUri: redirectUrl });
    }

    private handleRedirectResponse(response: AuthenticationResult): void {
        if (response) {
            this.authService.instance.setActiveAccount(response.account);
            this.loginDisplay = true;
            this.updateLogoutState(false);
            this.sessionTimeoutService.resetTimers();
        }
    }

    private handleAccountGuidOrRedirect(): boolean {
        const account = this.authService.instance.getAllAccounts()[0];
        if (account) {
            this.handleExistingAccount(account);
            return true;
        }

        return this.handleMissingAccount();
    }

    private handleExistingAccount(account: AccountInfo): void {
        this.authService.instance.setActiveAccount(account);

        const claimReference = account.idTokenClaims?.['extension_CaseReference'];
        if (claimReference) {
            this.router.navigate([`/home/${claimReference}`]);
        }
        else {
            this.redirectToErrorPage();
        }
    }

    private redirectToErrorPage(): void {
        this.router.navigate(['/error'], {
            queryParams: {
                headerText: 'Error - Missing Claim Reference',
                contentText: 'We could not find a claim reference associated with your account. Please contact support for assistance.'
            }
        });
    }

    private handleMissingAccount(): boolean {
        const stateServiceGuid = this.stateService.guid?.toUpperCase();
        const currentGuid = this.extractGuidFromUrl();
        const guid = stateServiceGuid || currentGuid;

        if (!guid || guid === environment.defaultGuid) {
            this.router.navigate([`/${environment.defaultGuid}/landing`]);
            return false;
        }
        if (!this.isValidGuid(guid)) {
            this.router.navigate([`/${guid}/invalid`]);
            return false;
        }
        if (!this.isSigningUp) {
            this.router.navigate([`/${guid}/login`]);
            return false;
        }

        return true;
    }

    private isValidGuid(guid: string | null | undefined): boolean {
        const guidRegex = /^(?:\{{0,1}(?:[0-9a-fA-F]){8}-(?:[0-9a-fA-F]){4}-(?:[0-9a-fA-F]){4}-(?:[0-9a-fA-F]){4}-(?:[0-9a-fA-F]){12}\}{0,1})$/;
        return guid !== null && guid !== undefined && guidRegex.test(guid);
    }

    private extractGuidFromUrl(): string | null {
        const urlSegments = this.router.url.split('/');
        return urlSegments.find(segment => this.isValidGuid(segment))?.toUpperCase() || null;
    }

    private subscribeToMsalEvents(): void {
        this.msalBroadcastService.msalSubject$
            .pipe(takeUntil(this._destroying$))
            .subscribe(() => {
                this.setLoginDisplay();
                this.sessionTimeoutService.resetTimers();
            });
    }

    private setLoginDisplay(): void {
        this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
        this.stateService.$loginDisplay.next(this.loginDisplay);
    }

    private listenToLoading(): void {
        this.loadingService.loadingSub.subscribe((loading) => {
            this.loading = loading;
        });
    }

    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }
    
    private updateLogoutState(isValidLogout: boolean): void {
        this.stateService.$loginDisplay.next(isValidLogout);
        this.stateService.isSigningUp = isValidLogout;
        localStorage.setItem('signingUp', JSON.stringify(isValidLogout));
    }
}